import React from "react";

// The Action column of a list (edit, delete, view, sign in as...).
//
// react-data-table-component shares the width out evenly and clips whatever
// does not fit, so on a list with many columns the last buttons in Action
// were simply cut off: the delete button only appeared on a very wide
// screen. Every table component passes its columns through fitActionColumn,
// which makes the Action column as wide as the buttons in its rows and lets
// its content overflow rather than be clipped. It also gets the column id
// "action", which css/admin.css pins to the right edge: on a narrower screen
// the list scrolls sideways under it, but the buttons stay in view.

const ICON_BUTTON = 32; // a round icon button plus the gap after it
const MIN_WIDTH = 96;

const textOf = (node) => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  return React.isValidElement(node) ? textOf(node.props?.children) : "";
};

// Width the buttons and links inside an action cell need, in pixels.
const widthOf = (node) => {
  if (node == null || typeof node === "boolean") return 0;
  if (Array.isArray(node)) return node.reduce((w, child) => w + widthOf(child), 0);
  if (!React.isValidElement(node)) return 0;
  const { type, props = {} } = node;
  const cls = String(props.className || "");
  const control =
    type === "button" ||
    type === "a" ||
    type === "select" ||
    props.to !== undefined ||
    /(^|[\s-])btn\b|\bbutton\b/.test(cls);
  if (control) {
    const text = textOf(props.children).trim();
    // icon-only buttons are round; a labelled one ("Add Term") grows with it
    return text.length > 1 ? Math.max(ICON_BUTTON, text.length * 7 + 28) : ICON_BUTTON;
  }
  return widthOf(props.children);
};

const isAction = (col) =>
  col &&
  typeof col === "object" &&
  (col.selector === "action" || /^actions?$/i.test(String(col.name || "").trim()));

export const fitActionColumn = (columns, rows) =>
  (columns || []).map((col) => {
    if (!isAction(col)) return col;
    let widest = 0;
    for (const row of (rows || []).slice(0, 50)) {
      let content = row?.action;
      if (typeof col.cell === "function") {
        try {
          content = col.cell(row);
        } catch (e) {
          content = null;
        }
      }
      widest = Math.max(widest, widthOf(content));
    }
    const width = Math.max(MIN_WIDTH, widest + 20);
    return {
      id: "action",
      ...col,
      allowOverflow: true,
      minWidth: col.minWidth || `${width}px`,
    };
  });
